import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { delegateTasksForStaff } from 'api/Wizard/Wizard';

import { optionify } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import Modal from 'components/shared/Modal/Modal';
import Select from 'components/shared/Select/Select';
import Textarea from 'components/shared/Textarea/Textarea';

const TaskDelegationModal = ({
  isOpen,
  handleClose,
  instanceId,
  step_ids,
  substep_ids,
  assignments,
  isRowClick = false,
  onDelegateSuccess
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [initialUserIds, setInitialUserIds] = useState([]);
  const navigate = useNavigate();
  const { practice } = useOutletContext();

  const usersList = optionify(practice?.members, 'professionalFullName');

  const selectPractitioners = (e) => {
    setSelectedUsers(e);
  };

  const stateCleanup = () => {
    setSelectedUsers([]);
    setNote('');
    setDueDate(null);
    setInitialUserIds([]);
  };

  const { mutateAsync } = useCustomMutation({
    mutationFn: (data) => delegateTasksForStaff(navigate, data),
    invalidateQueryKey: ['wizardInstanceDelegation']
  });

  const handleChangeEndOnDate = (date) => {
    setDueDate(date);
  };

  const handleSubstepDelegation = async () => {
    const currentUserIds = selectedUsers.map((u) => u.value);
    const usersToAdd = currentUserIds.filter((id) => !initialUserIds.includes(id));
    const usersToRemove = initialUserIds.filter((id) => !currentUserIds.includes(id));

    if (usersToAdd.length === 0 && usersToRemove.length === 0) {
      handleClose();
      return;
    }

    const basePayload = {
      wizard_instance_id: instanceId,
      practice_id: practice.id,
      step_ids,
      substep_ids
    };

    try {
      if (usersToRemove.length > 0) {
        await mutateAsync({ ...basePayload, userIds: usersToRemove, operation: 'delete' });
      }

      if (usersToAdd.length > 0) {
        await mutateAsync({
          ...basePayload,
          userIds: usersToAdd,
          note,
          due_date: dueDate,
          operation: 'add'
        });
      }

      stateCleanup();
      onDelegateSuccess?.();
      handleClose();
    } catch (error) {
      // Error handling is done by useCustomMutation
    }
  };

  useEffect(() => {
    if (isOpen && assignments && assignments.length > 0) {
      const users = assignments.map((a) => ({
        value: a.user_id,
        label: a.first_name + ' ' + a.last_name
      }));
      setSelectedUsers(users);
      setInitialUserIds(assignments.map((a) => a.user_id));

      // Only populate note and due_date for row clicks (single item)
      if (isRowClick) {
        setNote(assignments?.[0]?.note || '');
        setDueDate(assignments?.[0]?.due_date || null);
      } else {
        // Multi-select: clear note and due date
        setNote('');
        setDueDate(null);
      }
    } else if (isOpen) {
      // No assignments - clear everything
      stateCleanup();
    }
  }, [isOpen, assignments, isRowClick]);

  // Cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      stateCleanup();
    }
  }, [isOpen]);

  const currentUserIds = selectedUsers.map((u) => u.value);
  const hasRemovals = initialUserIds.some((id) => !currentUserIds.includes(id));
  const hasAdditions = currentUserIds.some((id) => !initialUserIds.includes(id));
  const hasChanges = hasRemovals || hasAdditions;

  const isFormValid = hasChanges && (selectedUsers.length === 0 || dueDate);

  return (
    <Modal
      title="Delegate"
      slideFromRight
      menuPortalTarget={document.body}
      isOpen={isOpen}
      footer={
        <div className="flex gap-4">
          <Button text="Close" color="neutral" onClick={handleClose} style="outlined" />
          <Button
            text={
              hasRemovals && !hasAdditions
                ? 'Remove'
                : hasRemovals && hasAdditions
                  ? 'Update'
                  : 'Delegate'
            }
            color={hasRemovals && !hasAdditions ? 'danger' : 'primary'}
            onClick={handleSubstepDelegation}
            isDisabled={!isFormValid}
          />
        </div>
      }
      handleClose={handleClose}>
      <Select
        inputId="staff"
        value={selectedUsers}
        options={usersList}
        onChange={selectPractitioners}
        isMulti
        required
        label="Staff Members"
        placeholder="Select and submit"
      />
      <Textarea
        textareaClassName="min-h-[100px]"
        placeholder="Note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        label="Leave a message to explain this task in details."
      />
      <DateInputPicker
        label="Due Date"
        dateFormat="MM/dd/yyyy"
        type="string"
        onChange={handleChangeEndOnDate}
        required
        value={dueDate}
      />
    </Modal>
  );
};

export default TaskDelegationModal;
